/**
 * Client-side metadata synchronisation.
 *
 * Django already renders the correct `<head>` for the initial document (see
 * `apps/core/views.py::AppShellView`), which is what crawlers and link-preview bots read. This
 * component keeps the tags correct after *client-side* navigation, when no new document is
 * fetched. Both paths resolve metadata from the same `/api/seo/resolve/` logic, so they agree.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { siteApi } from '@/services/api';

function upsertMeta(selector: string, attribute: 'name' | 'property', key: string, content: string): void {
  if (!content) {
    document.head.querySelector(selector)?.remove();
    return;
  }
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

const STRUCTURED_DATA_ID = 'shyena-structured-data';

/**
 * Overrides for metadata the API cannot know — for example a filtered listing whose title should
 * reflect the active filter. Passing nothing uses the server-resolved values verbatim.
 */
export interface HeadOverrides {
  title?: string;
  description?: string;
  noindex?: boolean;
}

export function Head({ overrides }: { overrides?: HeadOverrides } = {}) {
  const { pathname } = useLocation();

  const { data } = useQuery({
    queryKey: ['seo', pathname],
    queryFn: () => siteApi.resolveMeta(pathname),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;

    const title = overrides?.title ?? data.title;
    const description = overrides?.description ?? data.description;
    const robots = overrides?.noindex ? 'noindex, nofollow' : data.robots;

    document.title = title;
    upsertMeta('meta[name="description"]', 'name', 'description', description);
    upsertMeta('meta[name="robots"]', 'name', 'robots', robots);
    upsertMeta('meta[name="keywords"]', 'name', 'keywords', data.keywords);
    upsertLink('canonical', data.canonical);

    upsertMeta('meta[property="og:title"]', 'property', 'og:title', overrides?.title ?? data.og.title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', overrides?.description ?? data.og.description);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', data.og.type);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', data.canonical);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', data.og.image);

    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', data.twitter.card);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', overrides?.title ?? data.twitter.title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', overrides?.description ?? data.twitter.description);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', data.twitter.image);

    // Replace, rather than append, so navigating never accumulates stale JSON-LD blocks.
    document.getElementById(STRUCTURED_DATA_ID)?.remove();
    if (data.structured_data.length > 0) {
      const script = document.createElement('script');
      script.id = STRUCTURED_DATA_ID;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data.structured_data.length === 1 ? data.structured_data[0] : data.structured_data);
      document.head.appendChild(script);
    }
  }, [data, overrides?.title, overrides?.description, overrides?.noindex]);

  return null;
}
